/**
 * SEO Landing Page - Stillasfirma Oslo (B2B)
 * Public page for attracting business clients - contractors, developers, construction companies
 */
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import {
  Building2,
  Home,
  MapPin,
  CheckCircle2,
  Wrench,
  Shield,
  Phone,
  Mail,
  ChevronRight,
  Star,
  Award,
  FileText,
  ArrowRight,
  Users,
  HardHat,
  ClipboardCheck,
  Calendar,
  Send,
  ImageIcon,
  Play
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function StillasfirmaOslo() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  
  // Banner state
  const [banner, setBanner] = useState(null);
  
  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    project_address: '',
    description: '',
    phone: '',
    email: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [images, setImages] = useState([]);
  
  // Fetch active banner on mount
  useEffect(() => {
    const fetchBanner = async () => {
      try {
        const res = await fetch(`${API}/api/banner/active`);
        if (res.ok) {
          const data = await res.json();
          if (data.active && data.banner && data.banner.pages?.includes("b2b")) {
            setBanner(data.banner);
          }
        }
      } catch (error) {
        console.debug("Banner fetch failed:", error);
      }
    };
    fetchBanner();
  }, []);
  const [uploading, setUploading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const remainingSlots = 5 - images.length;
    if (files.length > remainingSlots) {
      toast.error(`Du kan bare legge til ${remainingSlots} bilde(r) til (maks 5)`);
      return;
    }

    setUploading(true);
    const uploadedUrls = [];
    
    // Upload one by one (more reliable than parallel)
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} er for stor (maks 5MB)`);
        continue;
      }
      
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('folder', 'public_quotes');
        
        const res = await fetch(`${API_URL}/api/projects-v3/public-upload`, {
          method: 'POST',
          body: formDataUpload
        });
        
        if (res.ok) {
          const data = await res.json();
          uploadedUrls.push(data.secure_url);
          toast.success(`Bilde ${i + 1}/${files.length} lastet opp`);
        } else {
          toast.error(`Kunne ikke laste opp ${file.name}`);
        }
      } catch (error) {
        toast.error(`Feil ved opplasting av ${file.name}`);
      }
    }
    
    if (uploadedUrls.length > 0) {
      setImages(prev => [...prev, ...uploadedUrls]);
    }
    setUploading(false);
    
    // Reset input so same files can be selected again
    e.target.value = '';
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/stillastjenester/public-quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          source_page: 'b2b',
          images: images
        })
      });

      if (!res.ok) throw new Error('Failed to submit');
      
      // Google Ads Conversion Tracking
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-18035432072/zLNqCNSS8Y0cEIi1-5dD',
          'value': 1.0,
          'currency': 'NOK'
        });
      }
      toast.success('Takk! Vi tar kontakt snart.');
      setFormData({
        company_name: '',
        contact_person: '',
        project_address: '',
        description: '',
        phone: '',
        email: ''
      });
      setImages([]);
    } catch (error) {
      toast.error('Kunne ikke sende forespørsel. Prøv igjen.');
    } finally {
      setSubmitting(false);
    }
  };

  // FAQ data for display (Norwegian only)
  const faqData = [
    { q: "Leverer dere stillas til store byggeprosjekter?", a: "Ja, vi har kapasitet og erfaring med større byggeprosjekter i Oslo og Viken-regionen." },
    { q: "Hva slags stillas bruker dere?", a: "Vi bruker både LAYHER (lett stillas) og HAKI (tungt stillas) for maksimal sikkerhet og fleksibilitet på alle våre prosjekter." },
    { q: "Kan dere bistå i planleggingsfasen av stillasprosjektet?", a: "Absolutt – vi deltar gjerne tidlig i prosjektet for å sikre optimal løsning og effektiv gjennomføring." },
    { q: "Hvor raskt kan dere levere stillas?", a: "Leveringstid avhenger av prosjektets størrelse og kompleksitet. For standard prosjekter kan vi ofte levere innen kort tid. Ta kontakt for en konkret vurdering." },
    { q: "Jobber dere etter NS-standarder og HMS-krav?", a: "Ja, vi følger alle relevante NS-standarder og HMS-krav. Sikkerhet er vår høyeste prioritet på alle prosjekter." }
  ];

  // FAQ Schema for rich results (always in Norwegian for Google.no)
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Leverer dere stillas til store byggeprosjekter?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ja, vi har kapasitet og erfaring med større byggeprosjekter i Oslo og Viken-regionen."
        }
      },
      {
        "@type": "Question",
        "name": "Hva slags stillas bruker dere?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Vi bruker både LAYHER (lett stillas) og HAKI (tungt stillas) for maksimal sikkerhet og fleksibilitet på alle våre prosjekter."
        }
      },
      {
        "@type": "Question",
        "name": "Kan dere bistå i planleggingsfasen av stillasprosjektet?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutt – vi deltar gjerne tidlig i prosjektet for å sikre optimal løsning og effektiv gjennomføring."
        }
      },
      {
        "@type": "Question",
        "name": "Hvor raskt kan dere levere stillas?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Leveringstid avhenger av prosjektets størrelse og kompleksitet. For standard prosjekter kan vi ofte levere innen kort tid. Ta kontakt for en konkret vurdering."
        }
      },
      {
        "@type": "Question",
        "name": "Jobber dere etter NS-standarder og HMS-krav?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ja, vi følger alle relevante NS-standarder og HMS-krav. Sikkerhet er vår høyeste prioritet på alle prosjekter."
        }
      }
    ]
  };

  // LocalBusiness Schema for B2B
  const businessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "AllStillas",
    "description": "Profesjonelt stillasfirma i Oslo for entreprenører og byggeprosjekter. LAYHER og HAKI stillas, fokus på HMS og effektiv levering.",
    "url": "https://allstillas.no",
    "telephone": "+47 93 92 28 19",
    "email": "support@allstillas.no",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Oslo",
      "addressRegion": "Oslo",
      "addressCountry": "NO"
    },
    "areaServed": ["Oslo", "Viken"],
    "serviceType": ["Scaffolding Services", "LAYHER Scaffolding", "HAKI Scaffolding", "Construction Support"]
  };
  
  return (
    <>
      {/* SEO Meta Tags - Keep in Norwegian for Google.no */}
      <Helmet>
        <title>Stillasfirma i Oslo | Stillastjenester for bedrifter – AllStillas</title>
        <meta 
          name="description" 
          content="Profesjonelt stillasfirma i Oslo for entreprenører og byggeprosjekter. LAYHER og HAKI stillas, fokus på HMS og effektiv levering. Be om tilbud."
        />
        <meta name="keywords" content="stillasfirma Oslo, stillastjenester bedrift, stillas byggeprosjekt, LAYHER stillas Oslo, HAKI stillas Oslo, stillas entreprenør, stillasutleie Oslo" />
        <link rel="canonical" href="https://allstillas.no/stillasfirma-oslo" />
        
        {/* hreflang for language versions - NO is primary, EN/RO are alternates */}
        <link rel="alternate" hreflang="no" href="https://allstillas.no/stillasfirma-oslo" />
        <link rel="alternate" hreflang="en" href="https://allstillas.no/stillasfirma-oslo?lang=en" />
        <link rel="alternate" hreflang="x-default" href="https://allstillas.no/stillasfirma-oslo" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Stillasfirma i Oslo | Stillastjenester for bedrifter – AllStillas" />
        <meta property="og:description" content="Profesjonelt stillasfirma i Oslo for entreprenører og byggeprosjekter. LAYHER og HAKI stillas, fokus på HMS og effektiv levering." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://allstillas.no/stillasfirma-oslo" />
        <meta property="og:locale" content="nb_NO" />
        
        {/* LocalBusiness Schema */}
        <script type="application/ld+json">
          {JSON.stringify(businessSchema)}
        </script>
        
        {/* FAQ Schema for Rich Results */}
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#050505]">
        {/* Navigation Bar */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-md border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-1">
                <div className="px-2 py-1 bg-[#0A0A0A] border border-[#23F7DD]/50 rounded flex items-center justify-center">
                  <span className="text-[#23F7DD] font-bold text-base font-['Unbounded']">All</span>
                </div>
                <span className="text-lg font-bold font-['Unbounded'] text-white">Stillas</span>
              </Link>
              
              <div className="flex items-center gap-2 sm:gap-3">
                <Link to="/">
                  <Button variant="ghost" className="text-white hover:text-[#23F7DD] hidden sm:inline-flex">
                    {t("platformGuide.home")}
                  </Button>
                </Link>
                <Link to="/hvordan-det-fungerer">
                  <Button variant="ghost" className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 gap-2 font-semibold hidden sm:inline-flex">
                    <Play className="w-4 h-4" />{t("platformGuide.home") === "Hjem" ? "Hvordan det fungerer" : "How it works"}
                  </Button>
                </Link>
                <Link to="/hvordan-det-fungerer">
                  <Button size="sm" className="bg-orange-500 text-white hover:bg-orange-400 font-bold px-3 text-xs sm:hidden">
                    Info
                  </Button>
                </Link>
                <LanguageSwitcher variant="full" className="text-white" />
                <Link to="/login">
                  <Button variant="ghost" className="text-white hover:text-[#23F7DD] hidden sm:inline-flex">
                    {t("stillasfirma.login")}
                  </Button>
                </Link>
                <a href="#tilbud">
                  <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold text-sm">
                    {t("stillasfirma.getQuote")}
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </nav>

        {/* PROMO BANNER - Dynamic from Admin */}
        {banner && (
          <div 
            className="fixed top-14 left-0 right-0 z-40 py-2 px-4 shadow-lg"
            style={{ 
              background: `linear-gradient(90deg, ${banner.background_color}, ${banner.background_color}CC, ${banner.background_color})`,
              fontFamily: banner.font_family === 'unbounded' ? "'Unbounded', sans-serif" : 
                         banner.font_family === 'mono' ? "monospace" : "inherit"
            }}
          >
            <div className="max-w-6xl mx-auto flex items-center justify-center gap-2 flex-wrap text-center">
              <span className="text-lg">🎉</span>
              <span 
                className="font-bold"
                style={{ 
                  color: banner.text_color || '#050505',
                  fontSize: banner.title_size || '14px'
                }}
              >
                {lang === "no" ? banner.title_no : banner.title_en}
              </span>
              {banner.discount_text && (
                <span 
                  className="bg-[#050505] font-black px-3 py-0.5 rounded-full"
                  style={{ 
                    color: banner.background_color,
                    fontSize: '14px'
                  }}
                >
                  {banner.discount_text}
                </span>
              )}
              <span 
                style={{ 
                  color: banner.text_color || '#050505',
                  fontSize: banner.desc_size || '12px'
                }}
              >
                {lang === "no" ? banner.description_no : banner.description_en}
              </span>
              {banner.days_remaining > 0 && (
                <span 
                  className="opacity-70"
                  style={{ 
                    color: banner.text_color || '#050505',
                    fontSize: '12px'
                  }}
                >
                  ({banner.days_remaining} {lang === "no" ? "dager igjen" : "days left"})
                </span>
              )}
              <span className="text-lg">🔥</span>
            </div>
          </div>
        )}

        {/* Hero Section - adjust padding if banner is active */}
        <section className={`relative ${banner ? 'pt-40' : 'pt-32'} pb-20 px-4 overflow-hidden`}>
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#23F7DD]/5 via-transparent to-transparent" />
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#23F7DD]/10 rounded-full blur-3xl" />
          <div className="absolute top-40 right-1/4 w-64 h-64 bg-[#4F46E5]/10 rounded-full blur-3xl" />
          
          <div className="max-w-4xl mx-auto text-center relative z-10">
            {/* Badge with Cross-Link */}
            <div className="flex items-center justify-center gap-3 mb-6 flex-wrap">
              {/* Current Page Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#4F46E5]/10 border border-[#4F46E5]/30 rounded-full">
                <Building2 className="w-4 h-4 text-[#4F46E5]" />
                <span className="text-sm text-[#4F46E5] font-medium">{t("stillasfirma.badge")}</span>
              </div>
              
              {/* Cross-link to Private Page */}
              <Link to="/stillas-privat-oslo">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F7931A]/10 border border-[#F7931A]/30 rounded-full hover:bg-[#F7931A]/20 transition-colors cursor-pointer">
                  <Home className="w-4 h-4 text-[#F7931A]" />
                  <span className="text-sm text-[#F7931A] font-medium">{t("stillasfirma.forPrivateLink")}</span>
                  <ChevronRight className="w-4 h-4 text-[#F7931A]" />
                </div>
              </Link>
            </div>
            
            {/* H1 - Main SEO Title */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Unbounded'] font-bold text-white mb-6 leading-tight">
              {t("stillasfirma.heroTitle")}
              <span className="block text-[#23F7DD] mt-2">{t("stillasfirma.heroSubtitle")}</span>
            </h1>
            
            {/* Intro Text */}
            <p className="text-lg sm:text-xl text-[#94A3B8] mb-4 max-w-2xl mx-auto leading-relaxed">
              {t("stillasfirma.heroText")}
            </p>
            
            <p className="text-[#94A3B8] text-base mb-8 max-w-2xl mx-auto">
              {t("stillasfirma.heroText2")}
            </p>
            
            {/* Primary CTA */}
            <a href="#tilbud">
              <Button 
                size="lg" 
                className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold text-lg px-8 py-6 rounded-xl shadow-lg shadow-[#23F7DD]/20 group"
                data-testid="hero-cta-quote"
              >
                <FileText className="w-5 h-5 mr-2" />
                {t("stillasfirma.ctaQuote")}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </div>
        </section>

        {/* Who Is This For Section */}
        <section className="py-20 px-4 bg-[#0A0A0A]">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-['Unbounded'] font-bold text-white mb-4">
                🏢 {t("stillasfirma.whoIsThisFor")}
              </h2>
              <p className="text-[#94A3B8] max-w-2xl mx-auto">
                {t("stillasfirma.whoIsThisForSubtitle")}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: <Building2 className="w-6 h-6" />, text: t("stillasfirma.whoItem1") },
                { icon: <Users className="w-6 h-6" />, text: t("stillasfirma.whoItem2") },
                { icon: <HardHat className="w-6 h-6" />, text: t("stillasfirma.whoItem3") },
                { icon: <ClipboardCheck className="w-6 h-6" />, text: t("stillasfirma.whoItem4") },
              ].map((item, i) => (
                <Card key={i} className="bg-white/5 border-white/10 hover:border-[#4F46E5]/30 transition-colors">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 bg-[#4F46E5]/10 rounded-xl flex items-center justify-center mx-auto mb-4 text-[#4F46E5]">
                      {item.icon}
                    </div>
                    <p className="text-white font-medium">{item.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            <p className="text-center text-[#23F7DD] mt-8 text-lg">
              👉 {t("stillasfirma.smallProjectsNote")}
            </p>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <h2 className="text-3xl sm:text-4xl font-['Unbounded'] font-bold text-white mb-6">
                  🔧 {t("stillasfirma.servicesTitle")}
                </h2>
                <p className="text-[#94A3B8] mb-6">{t("stillasfirma.servicesSubtitle")}</p>
                <ul className="space-y-4">
                  {[
                    t("stillasfirma.serviceItem1"),
                    t("stillasfirma.serviceItem2"),
                    t("stillasfirma.serviceItem3"),
                    t("stillasfirma.serviceItem4"),
                    t("stillasfirma.serviceItem5")
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-lg text-[#94A3B8]">
                      <CheckCircle2 className="w-5 h-5 text-[#23F7DD] flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h2 className="text-3xl sm:text-4xl font-['Unbounded'] font-bold text-white mb-6">
                  🛡️ {t("stillasfirma.safetyTitle")}
                </h2>
                <p className="text-[#94A3B8] mb-6">{t("stillasfirma.safetySubtitle")}</p>
                <ul className="space-y-4">
                  {[
                    t("stillasfirma.safetyItem1"),
                    t("stillasfirma.safetyItem2"),
                    t("stillasfirma.safetyItem3"),
                    t("stillasfirma.safetyItem4")
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-lg text-[#94A3B8]">
                      <Shield className="w-5 h-5 text-[#4F46E5] flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Work Area Section */}
        <section className="py-20 px-4 bg-[#0A0A0A]">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-['Unbounded'] font-bold text-white mb-6">
              📍 {t("stillasfirma.workAreaTitle")}
            </h2>
            <p className="text-[#94A3B8] text-lg mb-8">
              {t("stillasfirma.workAreaSubtitle")}
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {[t("stillasfirma.workAreaOslo"), t("stillasfirma.workAreaViken"), t("stillasfirma.workAreaNearby")].map((area, i) => (
                <div 
                  key={i}
                  className="px-6 py-3 bg-white/5 border border-white/10 rounded-full text-white font-medium flex items-center gap-2"
                >
                  <MapPin className="w-4 h-4 text-[#4F46E5]" />
                  {area}
                </div>
              ))}
            </div>
            
            {/* SEO Local Paragraph - Important for Google Local */}
            <p className="text-[#64748B] text-sm max-w-2xl mx-auto leading-relaxed">
              {t("stillasfirma.workAreaSeo")}
            </p>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-['Unbounded'] font-bold text-white mb-12 text-center">
              ⭐ {t("stillasfirma.whyChooseTitle")}
            </h2>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: <Award className="w-6 h-6" />, text: t("stillasfirma.whyItem1") },
                { icon: <Wrench className="w-6 h-6" />, text: t("stillasfirma.whyItem2") },
                { icon: <Users className="w-6 h-6" />, text: t("stillasfirma.whyItem3") },
                { icon: <Calendar className="w-6 h-6" />, text: t("stillasfirma.whyItem4") },
                { icon: <CheckCircle2 className="w-6 h-6" />, text: t("stillasfirma.whyItem5") },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-white/5 rounded-xl border border-white/10">
                  <div className="text-[#23F7DD]">{item.icon}</div>
                  <span className="text-white">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* References Section */}
        <section className="py-20 px-4 bg-[#0A0A0A]">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-['Unbounded'] font-bold text-white mb-6">
              🧾 {t("stillasfirma.referencesTitle")}
            </h2>
            <p className="text-[#94A3B8] mb-8">{t("stillasfirma.referencesSubtitle")}</p>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                t("stillasfirma.refItem1"),
                t("stillasfirma.refItem2"),
                t("stillasfirma.refItem3"),
                t("stillasfirma.refItem4")
              ].map((item, i) => (
                <div key={i} className="px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white">
                  {item}
                </div>
              ))}
            </div>
            
            <Link to="/projects" className="inline-block mt-8">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                {t("stillasfirma.seeProjects")}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Quote Form Section */}
        <section id="tilbud" className="py-20 px-4">
          <div className="max-w-2xl mx-auto">
            <Card className="bg-gradient-to-br from-[#4F46E5]/10 to-[#23F7DD]/5 border-[#4F46E5]/30">
              <CardContent className="p-8 sm:p-12">
                <div className="text-center mb-8">
                  <FileText className="w-16 h-16 text-[#4F46E5] mx-auto mb-4" />
                  <h2 className="text-3xl font-['Unbounded'] font-bold text-white mb-2">
                    {t("stillasfirma.formTitle")}
                  </h2>
                  <p className="text-[#94A3B8]">{t("stillasfirma.formSubtitle")}</p>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white mb-2 block">{t("stillasfirma.companyName")}</Label>
                      <Input
                        value={formData.company_name}
                        onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                        placeholder={t("stillasfirma.companyPlaceholder")}
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-white mb-2 block">{t("stillasfirma.contactPerson")}</Label>
                      <Input
                        value={formData.contact_person}
                        onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                        placeholder={t("stillasfirma.contactPlaceholder")}
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-white mb-2 block">{t("stillasfirma.projectAddress")}</Label>
                    <Input
                      value={formData.project_address}
                      onChange={(e) => setFormData({...formData, project_address: e.target.value})}
                      placeholder={t("stillasfirma.addressPlaceholder")}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white mb-2 block">{t("stillasfirma.description")}</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder={t("stillasfirma.descriptionPlaceholder")}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40 min-h-[100px]"
                      required
                    />
                  </div>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white mb-2 block">{t("stillasfirma.phone")}</Label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder={t("stillasfirma.phonePlaceholder")}
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-white mb-2 block">{t("stillasfirma.email")}</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder={t("stillasfirma.emailPlaceholder")}
                        className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                        required
                      />
                    </div>
                  </div>

                  {/* Image Upload Section */}
                  <div>
                    <Label className="text-white mb-2 block">{t("stillasfirma.addImages")}</Label>
                    <div className="space-y-3">
                      <label className="flex items-center justify-center gap-2 px-4 py-3 bg-white/5 border border-dashed border-white/20 rounded-lg cursor-pointer hover:bg-white/10 transition-colors">
                        <ImageIcon className="w-5 h-5 text-[#4F46E5]" />
                        <span className="text-white/70">{uploading ? t("stillasfirma.sending") : t("stillasfirma.maxImages")}</span>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageUpload}
                          className="hidden"
                          disabled={uploading || images.length >= 5}
                        />
                      </label>
                      
                      {images.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {images.map((url, index) => (
                            <div key={index} className="relative group">
                              <img 
                                src={url} 
                                alt={`Upload ${index + 1}`} 
                                className="w-20 h-20 object-cover rounded-lg border border-white/20"
                              />
                              <button
                                type="button"
                                onClick={() => removeImage(index)}
                                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <Button 
                    type="submit"
                    size="lg"
                    className="w-full bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF] font-bold"
                    disabled={submitting || uploading}
                  >
                    {submitting ? (
                      <>{t("stillasfirma.sending")}</>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        {t("stillasfirma.sendRequest")}
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-4 bg-[#0A0A0A]">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-['Unbounded'] font-bold text-white mb-12 text-center">
              ❓ {t("stillasfirma.faqTitle")}
            </h2>
            
            <div className="space-y-6">
              {faqData.map((faq, i) => (
                <Card key={i} className="bg-white/5 border-white/10">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-bold text-white mb-2 flex items-start gap-2">
                      <span className="text-[#4F46E5]">Q:</span>
                      {faq.q}
                    </h3>
                    <p className="text-[#94A3B8] pl-6">
                      <span className="text-[#23F7DD] font-bold">A:</span> {faq.a}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-['Unbounded'] font-bold text-white mb-6">
              📞 {t("stillasfirma.contactTitle")}
            </h2>
            <p className="text-[#94A3B8] text-lg mb-8">
              {t("stillasfirma.contactText")}
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <a 
                href="mailto:support@allstillas.no"
                className="flex items-center gap-3 px-6 py-4 bg-white/5 border border-white/10 rounded-xl hover:border-[#4F46E5]/30 transition-colors"
              >
                <Mail className="w-5 h-5 text-[#4F46E5]" />
                <span className="text-white">support@allstillas.no</span>
              </a>
              <a 
                href="tel:+4793922819"
                className="flex items-center gap-3 px-6 py-4 bg-white/5 border border-white/10 rounded-xl hover:border-[#4F46E5]/30 transition-colors"
              >
                <Phone className="w-5 h-5 text-[#4F46E5]" />
                <span className="text-white">+47 93 92 28 19</span>
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 px-4 border-t border-white/10">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-2">
                <Link to="/" className="flex items-center gap-1">
                  <div className="px-2 py-1 bg-[#0A0A0A] border border-[#23F7DD]/50 rounded">
                    <span className="text-[#23F7DD] font-bold text-sm font-['Unbounded']">All</span>
                  </div>
                  <span className="text-sm font-bold font-['Unbounded'] text-white">Stillas</span>
                </Link>
                <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#23F7DD]/10 border border-[#23F7DD]/20 rounded-full">
                  <span className="text-[#23F7DD] text-[10px]">⚡</span>
                  <span className="text-[10px] text-[#94A3B8]">Powered by</span>
                  <span className="text-[10px] text-[#23F7DD]">Doserne AS</span>
                </div>
              </div>
              
              <p className="text-sm text-[#64748B]">© 2026 Doserne AS. All rights reserved.</p>
              
              <div className="flex items-center gap-4 text-sm text-[#64748B]">
                <Link to="/stillasjobb-oslo" className="hover:text-white transition-colors">{t("stillasfirma.career")}</Link>
                <Link to="/privacy" className="hover:text-white transition-colors">{t("stillasfirma.privacy")}</Link>
                <Link to="/terms" className="hover:text-white transition-colors">{t("stillasfirma.privacy")}</Link>
              </div>
            </div>
            
            {/* SEO Boost Text - Small, subtle, helps Google */}
            <p className="text-xs text-[#475569] text-center">
              {t("stillasfirma.seoText")}
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
